/**
 * Generic password validation against user names.
 * Rejects if password contains first or last name (case-insensitive), their meaningful parts,
 * optionally reversed or simple leet-transformed versions.
 *
 * Policy (default):
 *  - Minimum length 15 (mirrors existing regex in userRegistration.jsx)
 *  - At least one upper, lower, digit, special (#?!@$%^&*-)
 *  - Must not contain any meaningful (>=3 chars) part of first or last name
 *  - Must not contain the full first or last name (>=3 chars) even if part of a larger token
 *  - Reject simple obfuscations (basic leet mapping) if enableLeetCheck is true
 *  - Reject reversed name parts if enableReverseCheck is true
 *
 * @param {Object} params
 * @param {string} params.password
 * @param {string} [params.firstName]
 * @param {string} [params.lastName]
 * @param {Object} [params.options]
 * @param {number} [params.options.minLength=15]
 * @param {boolean} [params.options.enableLeetCheck=true]
 * @param {boolean} [params.options.enableReverseCheck=true]
 * @returns {{valid: boolean, reasons: string[]}}
 */
export function validatePasswordAgainstNames({
  password = '',
  firstName = '',
  lastName = '',
  options = {}
}) {
  const {
    minLength = 15,
    enableLeetCheck = true,
    enableReverseCheck = true
  } = options;

  const reasons = [];
  const trimmedPwd = password.trim();

  // Core complexity regex (matches existing screen requirement)
  const complexityRegex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).+$/;

  if (trimmedPwd.length < minLength) {
    reasons.push(`Password must be at least ${minLength} characters.`);
  }
  if (!complexityRegex.test(trimmedPwd)) {
    reasons.push('Password must include upper, lower, digit and special character (#?!@$%^&*-).');
  }

  const sanitize = (str) => str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s]/g, ' ') // keep alphanumerics; replace others with space
    .replace(/\s+/g, ' ');

  const meaningfulParts = (name) => sanitize(name)
    .split(' ')
    .filter(Boolean)
    .filter(p => p.length >= 3);

  const pwdSanitized = sanitize(trimmedPwd);

  const allNameParts = [
    ...meaningfulParts(firstName),
    ...meaningfulParts(lastName)
  ];

  const fullNames = [];
  const fnSan = sanitize(firstName);
  const lnSan = sanitize(lastName);
  if (fnSan.length >= 3) fullNames.push(fnSan);
  if (lnSan.length >= 3) fullNames.push(lnSan);

  const leetMap = { '0':'o','1':'l','3':'e','4':'a','5':'s','7':'t' };
  const deLeet = (str) => str.replace(/[013457]/g, ch => leetMap[ch] || ch);
  const pwdDeLeet = enableLeetCheck ? deLeet(pwdSanitized) : pwdSanitized;

  const containsAny = (source) => {
    for (const token of [...allNameParts, ...fullNames]) {
      if (token && source.includes(token)) {
        return token;
      }
      if (enableReverseCheck) {
        const rev = token.split('').reverse().join('');
        if (rev && source.includes(rev)) {
          return token;
        }
      }
    }
    return null;
  };

  const offending = containsAny(pwdSanitized) || containsAny(pwdDeLeet);
  if (offending) {
    reasons.push('Password must not contain first name or last name (or parts thereof).');
  }

  return {
    valid: reasons.length === 0,
    reasons
  };
}

/**
 * Convenience boolean-only checker used by UI forms.
 */
export function isPasswordAcceptable(password, firstName, lastName, options) {
  return validatePasswordAgainstNames({ password, firstName, lastName, options }).valid;
}

export default validatePasswordAgainstNames;
